import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChefHat, 
  Users, 
  ShoppingBag, 
  Heart,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Clock,
  Star,
  Utensils,
  Home as HomeIcon,
  ThumbsUp,
  Facebook,
  Twitter,
  Youtube,
  Instagram,
  Vote,
  Store,
  BookOpen,
  Shield
} from 'lucide-react';

// Carousel images
const CAROUSEL_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1742281257687-092746ad6021?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmb29kJTIwc3ByZWFkJTIwdG9wJTIwdmlld3xlbnwwfHx8fDE3Njc2MDUxOTl8MA&ixlib=rb-4.1.0&q=85",
    title: "Traditional Thali",
    subtitle: "A complete meal on a banana leaf"
  },
  {
    url: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1200",
    title: "Homemade Biryani",
    subtitle: "Aromatic rice with tender spices"
  },
  {
    url: "https://images.unsplash.com/photo-1567337710282-00832b415979?w=1200",
    title: "Festival Sweets",
    subtitle: "Celebrate with traditional mithai"
  }
];

const SELLER_IMAGE = "https://images.unsplash.com/photo-1596450513856-250e5f5c46cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB3b21hbiUyMGNvb2tpbmclMjBraXRjaGVuJTIwaGFwcHl8ZW58MHx8fHwxNzY3NjA1MjAwfDA&ixlib=rb-4.1.0&q=85";
const DEALER_IMAGE = "https://images.unsplash.com/photo-1758910536889-43ce7b3199fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzd2VldHMlMjBtaXRoYWklMjBzaG9wJTIwZGlzcGxheXxlbnwwfHx8fDE3Njc2MDUyMDJ8MA&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);

  // RuchiBox Platter - Features from PRD
  const platterItems = [
    {
      icon: Utensils,
      title: "Authentic Home Food",
      description: "Taste the love in every bite - recipes passed down through generations"
    },
    {
      icon: MapPin,
      title: "Hyperlocal Delivery",
      description: "Food from within your apartment complex - fresh, fast, and friendly"
    },
    {
      icon: Users,
      title: "Support Micro-Entrepreneurs",
      description: "Help your neighbors earn from their culinary skills"
    },
    {
      icon: Heart,
      title: "Community Connection",
      description: "Build bonds over shared meals and trusted recommendations"
    },
    {
      icon: Vote,
      title: "RuchiBox Poll",
      description: "Vote for bulk orders and get the best prices together",
      path: "/poll"
    },
    {
      icon: Store,
      title: "RuchiBox Bazaar",
      description: "Buy, sell, or donate items within your community",
      path: "/bazaar"
    },
    {
      icon: BookOpen,
      title: "RuchiBox Digest",
      description: "FAQs, balanced diet tips, and home remedies",
      path: "/digest"
    },
    {
      icon: Shield,
      title: "RuchiBox Promise",
      description: "Our commitment to quality, trust, and community standards",
      path: "/promise"
    }
  ];

  const roles = [
    {
      title: "I want to Order Food",
      description: "Discover delicious homemade meals from your neighbors",
      image: CAROUSEL_IMAGES[0].url,
      action: "Browse Food",
      role: "buyer"
    },
    {
      title: "I want to Sell Food",
      description: "Share your culinary skills and earn from home",
      image: SELLER_IMAGE,
      action: "Start Selling",
      role: "seller"
    },
    {
      title: "I'm a Vendor",
      description: "Reach customers in residential complexes",
      image: DEALER_IMAGE,
      action: "Partner with Us",
      role: "dealer"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="font-heading text-2xl font-bold text-foreground">RuchiBox</span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#platter" className="text-muted-foreground hover:text-foreground transition-colors">RuchiBox Platter</a>
              <a href="#popular" className="text-muted-foreground hover:text-foreground transition-colors">Popular</a>
              <a href="#recommendations" className="text-muted-foreground hover:text-foreground transition-colors">Recommendations</a>
            </nav>
            <Button 
              data-testid="login-btn"
              onClick={() => navigate('/auth')}
              className="rounded-full px-6 bg-primary hover:bg-primary/90"
            >
              Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Carousel Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Carousel Background */}
        <div className="absolute inset-0 z-0">
          {CAROUSEL_IMAGES.map((img, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={img.url} 
                alt={img.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent"></div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors"
          data-testid="carousel-prev"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-4 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors"
          data-testid="carousel-next"
        >
          <ArrowRight className="w-6 h-6 text-white" />
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {CAROUSEL_IMAGES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentSlide ? 'bg-primary w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold mb-6 animate-fade-in">
              Food from your community
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 animate-fade-in stagger-1">
              Savor the Taste of <span className="text-primary">Home</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-4 animate-fade-in stagger-2">
              <strong>{CAROUSEL_IMAGES[currentSlide].title}</strong> - {CAROUSEL_IMAGES[currentSlide].subtitle}
            </p>
            <p className="text-base text-muted-foreground mb-8 animate-fade-in stagger-2">
              Discover authentic homemade food from talented home cooks and trusted vendors in your apartment complex. 
              Fresh, delicious, and delivered right to your door.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in stagger-3">
              <Button 
                data-testid="get-started-btn"
                size="lg"
                onClick={() => navigate('/auth')}
                className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 btn-glow"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                data-testid="learn-more-btn"
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('platter')?.scrollIntoView({ behavior: 'smooth' })}
                className="rounded-full px-8 py-6 text-lg border-2 border-primary text-primary hover:bg-primary/10"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "500+", label: "Happy Families" },
              { value: "50+", label: "Home Cooks" },
              { value: "100+", label: "Daily Orders" },
              { value: "4.8", label: "Average Rating", icon: Star }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground">{stat.value}</span>
                  {stat.icon && <stat.icon className="w-6 h-6 text-primary-foreground fill-primary-foreground" />}
                </div>
                <p className="text-primary-foreground/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RuchiBox Platter Section */}
      <section id="platter" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              RuchiBox Platter
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A feast of benefits served fresh to your doorstep
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platterItems.map((item, idx) => (
              <Card 
                key={idx} 
                className={`card-hover border-none shadow-md bg-card ${item.path ? 'cursor-pointer' : ''}`}
                onClick={() => item.path && navigate(item.path)}
                data-testid={`platter-item-${idx}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {item.description}
                  </p>
                  {item.path && (
                    <Button variant="link" className="mt-2 text-primary p-0 h-auto">
                      Learn more <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Join Community Section */}
      <section id="recommendations" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Join Our Community
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Whether you want to order delicious food or share your culinary skills, there's a place for you
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {roles.map((role, idx) => (
              <Card 
                key={idx} 
                className="overflow-hidden card-hover cursor-pointer group"
                onClick={() => navigate('/auth', { state: { defaultRole: role.role } })}
                data-testid={`role-card-${role.role}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={role.image} 
                    alt={role.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent"></div>
                </div>
                <CardContent className="p-6 relative -mt-12 z-10">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {role.title}
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    {role.description}
                  </p>
                  <Button 
                    variant="secondary"
                    className="rounded-full w-full bg-secondary hover:bg-secondary/90"
                  >
                    {role.action}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="popular" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              How It Works
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: 1, title: "Register & Select Society", description: "Sign up with your mobile and choose your apartment complex", icon: HomeIcon },
              { step: 2, title: "Browse & Order", description: "Explore dishes from home cooks and vendors, add to cart", icon: ShoppingBag },
              { step: 3, title: "Enjoy Fresh Food", description: "Get your order delivered fresh from your neighbor's kitchen", icon: ThumbsUp }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                    <item.icon className="w-10 h-10 text-primary-foreground" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-sm">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Ready to Taste the Difference?
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-lg">
            Join thousands of happy families enjoying home-cooked meals from their community
          </p>
          <Button 
            data-testid="cta-get-started"
            size="lg"
            variant="secondary"
            onClick={() => navigate('/auth')}
            className="rounded-full px-10 py-6 text-lg font-bold"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <ChefHat className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-heading text-xl font-bold">RuchiBox</span>
              </div>
              <p className="text-background/60 text-sm">
                Bringing the warmth of home-cooked meals to your community
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Help</h4>
              <ul className="space-y-2 text-background/60 text-sm">
                <li><a href="#" className="hover:text-background transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-background transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-background transition-colors">FAQs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Links</h4>
              <ul className="space-y-2 text-background/60 text-sm">
                <li><a href="#" className="hover:text-background transition-colors">Home</a></li>
                <li><a href="#recommendations" className="hover:text-background transition-colors">Recommendations</a></li>
                <li><a href="#popular" className="hover:text-background transition-colors">Popular</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Follow</h4>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-background/20 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-background/20 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-background/20 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-background/20 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-background/10 mt-8 pt-8 text-center text-background/40 text-sm">
            <p>&copy; {new Date().getFullYear()} RuchiBox. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
